import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Option } from '@dxc-technology/halstack-angular';
import { METHOD } from '@shared/components/base-classes/base-group/constants';
import { Member } from '@shared/models/member';
import { AbstractHttpObservable } from '@shared/types/abstract';
import { HttpMethod, IHalResponse } from '@shared/types/hal-client';
import { MessageError, YesNo } from '@shared/types/type';
import { customComputed } from '@shared/utils/signal.util';
import _ from 'lodash';
import { from, Observable } from 'rxjs';
import { catchError, map, pluck, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CommonService } from './common.service';
import { ApplicationForm, SPAK_SECTION_KEY, SpakFormService } from './spak-form.service';

export type MedicalType =
  | 'Employee Unable to Work'
  | 'Employee Able to Work'
  | 'Employee Diagnosis (Last 5 Years)'
  | 'Dependent Unable to Perform Daily Activities'
  | 'Dependent Diagnosis (Last 5 Years)';

export type GroupedMedical = {
  cannotWorkEmployeeList: FORM_QUESTION_TABLE;
  canWorkEmployeeList: FORM_QUESTION_TABLE;
  diagnosedEmployeeList: FORM_QUESTION_TABLE;
  dependentsCannotWork: FORM_QUESTION_TABLE;
  dependentsAreDiagnosed: FORM_QUESTION_TABLE;
};
export type FORM_QUESTION_TABLE = {
  answerValue?: YesNo;
  rows: (CommonTableDetail | DependentsCannotWorkTableDetail | DependentsDiagnosedDetail)[];
};
export type SectionData = ApplicationForm;
export enum MedicalEnum {
  'Employee Unable to Work' = 'cannotWorkEmployeeList',
  'Employee Able to Work' = 'canWorkEmployeeList',
  'Employee Diagnosis (Last 5 Years)' = 'diagnosedEmployeeList',
  'Dependent Unable to Perform Daily Activities' = 'dependentsCannotWork',
  'Dependent Diagnosis (Last 5 Years)' = 'dependentsAreDiagnosed',
}

export enum ReverseMedicalEnum {
  'cannotWorkEmployeeList' = 'Employee Unable to Work',
  'canWorkEmployeeList' = 'Employee Able to Work',
  'diagnosedEmployeeList' = 'Employee Diagnosis (Last 5 Years)',
  'dependentsCannotWork' = 'Dependent Unable to Perform Daily Activities',
  'dependentsAreDiagnosed' = 'Dependent Diagnosis (Last 5 Years)',
}
export type MemberMecdialesponse = {
  memberId: string;
  medicalType: string;
  absenceDate: string;
  status: string;
  typeDisease: string;
  diagnosisDate: string;
};

export enum TABLE_NAME {
  EMPLOYEE_CANNOT_WORK,
  EMPLOYEE_CAN_WORK,
  EMPLOYEE_DIAGNOSED,
}

export type CommonTableDetail = {
  birthday: string;
  position: string;
  memberId: string;
  medicalType: string;
  fullName: string;
};
export type DependentsCannotWorkTableDetail = {
  memberId: string;
  birthday: string;
  fullName: string;
  status: string;
};
export type DependentsDiagnosedDetail = {
  memberId: string;
  birthday: string;
  fullName: string;
  status: string;
};
export type QuestionItem = {
  href: string;
  summary: Question;
};

export type Question = {
  Mandatory: string;
  answer_type: string;
  answer_value: string;
  category: string;
  choice_list: ['Yes', 'No'];
  has_sub_questions: boolean;
  id: string;
  loadingcode: string;
  q_text: string;
  req: string;
};

export type BenefitSummary = {
  insurancePlan: string;
  eligibility: { eligibilityDesc: string }[];
  product: BenefitSummaryProduct[];
};
export type BenefitSummaryProduct = {
  productCode: string;
  eligibility: {
    eligibilityDesc: string;
  };
  type: string;
};

@Injectable({
  providedIn: 'root',
})
export class CoverageInformationService implements AbstractHttpObservable {
  private readonly commonService = inject(CommonService);
  private readonly spakService = inject(SpakFormService);
  readonly proposal = toSignal(this.spakService.proposal$.pipe(takeUntilDestroyed()));
  readonly url = computed<string>(() => `${environment.API_PROPOSAL_SERVER_URL}/${this.proposal().id}`);
  readonly #memberOptions = signal<Option[]>([]);
  memberOptions = computed(() => this.#memberOptions());
  filteredMemberOptions = signal<Option[]>([]);
  statusReportErrors = customComputed<MessageError[]>(() => _.cloneDeep(this.spakService.statusReportErrors().get(SPAK_SECTION_KEY.SECTION5)));
  employeeListCount = signal<number>(0);
  dependentListCount = signal<number>(0);
  dependentList2Count = signal<number>(0);
  selected = signal<number>(0);
  employeeList = signal<any[]>([]);
  dependentList = signal<any[]>([]);
  dependentList2 = signal<any[]>([]);
  selectedMembers: WritableSignal<any[]> = signal([]);
  selectedMembers2: WritableSignal<any[]> = signal([]);

  commonHttpObservable<T = any>(
    url: string,
    options: { headers?: Record<string, string>; method: HttpMethod; body?: Record<string, any> } = { method: 'get' }
  ): Observable<T> {
    if (!_.get(options, 'headers.language')) {
      _.set(options, 'headers.language', this.spakService.currentSpakLanguage());
    }
    return this.commonService.commonHttpObservable(url, options);
  }

  getMemberOptions$(
    query: {
      relationShip: 'K' | 'P,A';
      fullName?: string;
      page: number;
      size: number;
    },
    keyOfList: 'employeeList' | 'dependentList' | 'dependentList2'
  ): Observable<Option[]> {
    const url = new URL(`${this.url()}/member`);
    for (const [key, value] of _(query).entries().value()) {
      url.searchParams.set(key, value + '');
    }

    return this.commonHttpObservable(url.href).pipe(
      tap((res: IHalResponse<Member>) => {
        this[`${keyOfList}Count`].set(res.body._links._count || 0);
      }),
      map((res: IHalResponse<Member>) => _.map(res.halResource.getItems(), (item) => item.summary)),
      map((summaries: Member[]) => {
        return _.map(summaries, ({ fullName, uuid, birthday, position, relationShipDesc }) => ({
          label: fullName,
          value: uuid,
          uuid,
          birthday,
          position,
          status: relationShipDesc,
          fullName,
        }));
      }),
      catchError((err) => {
        console.error(err);
        throw err;
      })
    );
  }

  getMedicalTable$(): Observable<GroupedMedical> {
    const applicationId = this.spakService.applicationFormData().uuid;
    const url = new URL(`${this.url()}/eKYC/applicationForm/${applicationId}/medicalTable`);

    const groupedMedical: GroupedMedical = {
      cannotWorkEmployeeList: {
        rows: [],
      },
      canWorkEmployeeList: {
        rows: [],
      },
      diagnosedEmployeeList: {
        rows: [],
      },
      dependentsCannotWork: {
        rows: [],
      },
      dependentsAreDiagnosed: {
        rows: [],
      },
    };

    return this.commonHttpObservable(url.href).pipe(
      map((res) => res.body),
      map((tables: any[]) => {
        return tables.reduce((prev, cur: MemberMecdialesponse) => {
          const key = MedicalEnum[cur.medicalType];
          prev[key].rows.push({
            ..._.pick(cur, ['memberId', 'medicalType', 'absenceDate', 'status', 'typeDisease', 'diagnosisDate', 'fullName', 'birthday', 'position']),
          });
          return prev;
        }, _.cloneDeep(groupedMedical));
      }),
      catchError((err) => {
        console.error(err);
        throw err;
      })
    );
  }

  deleteRow$(id: string) {
    const applicationId = this.spakService.applicationFormData().uuid;
    const url = `${this.url()}/eKYC/applicationForm/${applicationId}/medicalTable/${id}`;
    return this.commonHttpObservable(url, { method: 'del' });
  }

  patchRows$(body: any) {
    const applicationId = this.spakService.applicationFormData().uuid;
    const url = `${this.url()}/eKYC/applicationForm/${applicationId}/medicalTable`;
    return this.commonHttpObservable(url, { method: 'patch', body });
  }

  set memberOptionsS(options: Option[]) {
    this.#memberOptions.set(options);
  }

  getSectionData$(): Observable<SectionData> {
    const url = `${this.url()}/eKYC/applicationForm`;
    return this.commonHttpObservable(url).pipe(pluck('body'));
  }

  patchSectionData$(body: Partial<SectionData>, formId: string): Observable<SectionData> {
    const url = `${this.url()}/eKYC/applicationForm/${formId}`;
    return this.commonHttpObservable(url, { method: 'patch', body }).pipe(pluck('body'));
  }

  getTotalInsured$() {
    const url = `${this.url()}/totalMemberbyAgeRange`;
    return this.commonHttpObservable(url).pipe(
      pluck('body'),
      map((plans) => {
        const totalInsured = _.chain(plans)
          .map((plan) => (_.isEmpty(plan.termMemberSummary) ? plan.healthMemberSummary : plan.termMemberSummary))
          .flatMap()
          .reduce((total, ageRange) => {
            return total + Number(ageRange.femaleMainInsured) + Number(ageRange.maleMainInsured);
          }, 0)
          .value();

        return totalInsured;
      })
    );
  }

  getPlanBenefitSummaries$(lang: string): Observable<BenefitSummary[]> {
    const url = `${this.url()}/eKYC/applicationForm/planTable`;
    return this.commonService
      .commonHttpObservable(url, {
        headers: {
          language: lang,
        },
        method: 'get',
      })
      .pipe(pluck('body'));
  }

  getCities$(): Observable<Option[]> {
    const url = new URL(`${environment.API_GROUP_INTEGRATION_URL}/filterAddress`);
    url.searchParams.set('filterType', 'city');
    return this.commonHttpObservable(url.href, { method: 'post', body: {} }).pipe(
      pluck('body'),
      catchError((err) => {
        console.error(err);
        throw err;
      })
    );
  }

  createQSN$() {
    const url = `${environment.API_QSN}/questionnaire-sets`;
    let body = {
      coverages: {
        Coverages: [
          {
            Coverage: 'GTL1',
          },
        ],
      },
      lob: 'Group',
      customer_id: 'DXCBase',
      language: this.spakService.currentSpakLanguage() === 'bi' ? 'I' : 'E',
      contract_type: 'GTL',
      usage: 'SPAKSectionV',
      insured_data: {
        Insured_gender: 'Female',
        Insured_DOB: '1971-01-01',
      },
      scheme_effective_date: '2022-01-11',
      locale: 'en',
    };
    return from(this.commonService.callApiForQsn({ url, method: METHOD.POST, body }));
  }

  getQuestionnaires$(url: string) {
    return from(this.commonService.callApiForQsn({ url, method: METHOD.GET })).pipe(map((data) => data._links.item.href));
  }

  getQuestionnairesbyId$(url: string) {
    return from(this.commonService.callApiForQsn({ url, method: METHOD.GET })).pipe(map((data) => data._links['questionnaire:questions'].href));
  }

  getQuestion$(url: string) {
    return from(this.commonService.callApiForQsn({ url, method: METHOD.GET })).pipe(map((data) => data._links.item));
  }

  answerQuestion$(url: string, body: any) {
    return from(this.commonService.callApiForQsn({ url, method: METHOD.PATCH, body }));
  }
}
